/**
 * Form and action primitives.
 *
 * Every control is a real element — `<button>`, `<input>`, `<select>` — so
 * keyboard behaviour, form participation and screen-reader semantics come from
 * the platform rather than from re-implementation. Focus rings are inherited
 * from `index.css`, not removed here.
 */

import type {
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
  Ref,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from "react";
import { useId, useState } from "react";

type Variant = "primary" | "secondary" | "ghost" | "danger";

const VARIANTS: Record<Variant, string> = {
  // `brightness` rather than a darker hex, since T-38: a public page re-points
  // `--color-accent` at a client's brand, and a hover written as a hex would
  // turn the button back to ours. Opacity works on black, white and a brand alike.
  primary:
    "bg-accent text-inverse border border-accent hover:opacity-85 disabled:bg-ink-faint disabled:border-ink-faint disabled:hover:opacity-100",
  secondary:
    "bg-surface border border-rule-strong text-ink hover:bg-sunk hover:border-ink-faint",
  ghost: "bg-transparent text-ink-muted border border-transparent hover:bg-sunk hover:text-ink",
  danger:
    "bg-surface border border-rule-strong text-danger hover:bg-danger-soft hover:border-danger",
};

/** A button's look, for the one thing that must look like a button and be a
 * link: somewhere to go, which a `<button>` cannot be and an `<a>` inside one
 * is invalid. */
export function buttonClass(variant: Variant = "secondary"): string {
  return `inline-flex h-9 items-center gap-1.5 rounded-control px-3.5 text-md
        font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-60
        ${VARIANTS[variant]}`;
}

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  /** Shows a pending label and blocks repeat submits. */
  pending?: boolean;
  pendingLabel?: string;
}

export function Button({
  variant = "secondary",
  pending = false,
  pendingLabel = "Working…",
  children,
  className = "",
  disabled,
  // A `<button>` with no type is a submit button, and this admin nests
  // buttons inside forms everywhere — the person editor holds a whole gallery
  // of them. Biome's `useButtonType` enforces this on a raw element and
  // cannot see through a component that spreads props onto one, so the
  // default belongs here: submitting is something a form's own button asks
  // for, by saying `type="submit"`.
  type = "button",
  ...rest
}: ButtonProps) {
  return (
    <button
      type={type}
      // A submit button that does not say it is busy invites a second click,
      // and a second click is a second entry.
      aria-busy={pending || undefined}
      disabled={disabled ?? pending}
      className={`${buttonClass(variant)} ${className}`}
      {...rest}
    >
      {pending ? pendingLabel : children}
    </button>
  );
}

const CONTROL_BASE = `w-full rounded-control border bg-surface px-2.5 py-1.5 text-md text-ink
   transition-colors placeholder:text-ink-faint
   disabled:bg-sunk disabled:text-ink-muted`;

function ringFor(invalid: boolean): string {
  return invalid ? "border-danger" : "border-rule-strong hover:border-ink-faint";
}

interface FieldShellProps {
  label: string;
  htmlFor: string;
  // `| undefined` spelled out because exactOptionalPropertyTypes distinguishes
  // "absent" from "present and undefined", and callers forward optional values
  // straight through from a field descriptor.
  required?: boolean | undefined;
  help?: string | null | undefined;
  error?: string | undefined;
  /** Where the field sits in the grid around it — a column span, usually. The
   * shell owns its own internals; this is for the caller's layout. */
  className?: string | undefined;
  children: ReactNode;
}

/** Label, control, help and error — wired together by id. */
export function FieldShell({
  label,
  htmlFor,
  required,
  help,
  error,
  className = "",
  children,
}: FieldShellProps) {
  const helpId = `${htmlFor}-help`;
  const errorId = `${htmlFor}-error`;
  return (
    <div className={`grid content-start gap-1.5 ${className}`}>
      <label id={`${htmlFor}-label`} htmlFor={htmlFor} className="text-md font-medium text-ink">
        {label}
        {required && (
          <span className="ml-1 text-danger" aria-hidden="true">
            *
          </span>
        )}
        {required && <span className="sr-only"> (required)</span>}
      </label>
      {children}
      {help && !error && (
        <p id={helpId} className="text-sm text-ink-muted">
          {help}
        </p>
      )}
      {error && (
        // role="alert" so the message is announced when it appears, and the
        // colour is never the only signal that something is wrong.
        <p id={errorId} role="alert" className="text-sm font-medium text-danger">
          {error}
        </p>
      )}
    </div>
  );
}

interface TextInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "id"> {
  id: string;
  invalid?: boolean;
  ref?: Ref<HTMLInputElement>;
}

export function TextInput({ id, invalid = false, className = "", ...rest }: TextInputProps) {
  return (
    <input
      id={id}
      aria-invalid={invalid || undefined}
      aria-describedby={invalid ? `${id}-error` : `${id}-help`}
      className={`${CONTROL_BASE} ${ringFor(invalid)} ${className}`}
      {...rest}
    />
  );
}

interface TextAreaProps extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, "id"> {
  id: string;
  invalid?: boolean;
}

export function TextArea({ id, invalid = false, className = "", ...rest }: TextAreaProps) {
  return (
    <textarea
      id={id}
      rows={4}
      aria-invalid={invalid || undefined}
      aria-describedby={invalid ? `${id}-error` : `${id}-help`}
      className={`${CONTROL_BASE} ${ringFor(invalid)} resize-y leading-relaxed ${className}`}
      {...rest}
    />
  );
}

/** A choice, when what the client reads is not what the server stores.
 *
 * A plain string is both, which is the common case here — an event's status is
 * "draft" and reads as "draft". The pair is for the cases where it is not: a
 * registration field's kind is `select` and reads as "Choose one". */
export type Choice = string | { value: string; label: string };

interface SelectInputProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, "id"> {
  id: string;
  invalid?: boolean;
  options: Choice[];
  placeholder?: string;
}

export function SelectInput({
  id,
  invalid = false,
  options,
  placeholder = "Not set",
  required,
  className = "",
  ...rest
}: SelectInputProps) {
  return (
    <select
      id={id}
      required={required}
      aria-invalid={invalid || undefined}
      aria-describedby={invalid ? `${id}-error` : `${id}-help`}
      className={`${CONTROL_BASE} ${ringFor(invalid)} ${className}`}
      {...rest}
    >
      {/* A required choice offers no empty option to go back to: "Not set"
          would be a value the server refuses, and an event has a status either
          way. But until one is made it shows "Choose…", disabled — without it
          the browser draws the first option while the value is still empty,
          and the screen says an answer was given that will not be sent. Not
          when an option is already empty: the attendee filters' "Any status"
          is one, and means something. */}
      {!required && <option value="">{placeholder}</option>}
      {required &&
        rest.value === "" &&
        !options.some((o) => (typeof o === "string" ? o : o.value) === "") && (
          <option value="" disabled>
            Choose…
          </option>
        )}
      {options.map((option) => {
        const { value, label } = pair(option);
        return (
          <option key={value} value={value}>
            {label}
          </option>
        );
      })}
    </select>
  );
}

function pair(option: Choice): { value: string; label: string } {
  return typeof option === "string" ? { value: option, label: option } : option;
}

interface SearchSelectProps extends SelectInputProps {
  /** Names the search box, for a screen reader and as its placeholder. */
  searchLabel: string;
}

/** A select with a search box beside it, for a list too long to scroll: what is
 * typed narrows the options. The option chosen stays listed whatever is typed,
 * or the select would draw another option than the value it holds. */
export function SearchSelect({
  searchLabel,
  options,
  className = "",
  ...rest
}: SearchSelectProps) {
  const [query, setQuery] = useState("");
  const needle = query.trim().toLowerCase();
  const shown = needle
    ? options.filter((option) => {
        const { value, label } = pair(option);
        return value === rest.value || label.toLowerCase().includes(needle);
      })
    : options;
  return (
    <div className={`flex flex-wrap gap-2 ${className}`}>
      <TextInput
        id={`${rest.id}-search`}
        type="search"
        aria-label={searchLabel}
        placeholder={`${searchLabel}…`}
        value={query}
        className="min-w-32 flex-1"
        // Enter in a search box inside a card's form would save the card.
        onKeyDown={(event) => {
          if (event.key === "Enter") event.preventDefault();
        }}
        onChange={(event) => setQuery(event.target.value)}
      />
      <SelectInput {...rest} options={shown} className="min-w-40 flex-[2]" />
    </div>
  );
}

interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "id" | "type"> {
  label: string;
  help?: string | null;
}

export function Checkbox({ label, help, className = "", ...rest }: CheckboxProps) {
  const id = useId();
  return (
    <div className="grid gap-1.5">
      <div className="flex items-start gap-2">
        <input
          id={id}
          type="checkbox"
          className={`mt-0.5 size-4 rounded-control border-rule-strong text-accent
            accent-[var(--color-accent)] ${className}`}
          {...rest}
        />
        <label htmlFor={id} className="text-md text-ink">
          {label}
        </label>
      </div>
      {help && <p className="pl-6 text-sm text-ink-muted">{help}</p>}
    </div>
  );
}
